import { z } from 'zod';
import { CommercialProposalPaymentSchemeSchema } from './status.js';

/**
 * @aiPurpose customer-facing buyout/logistics display row on the public
 *   proposal sheet (#1194). A label plus an already-formatted display
 *   value string. The backend (#1195) formats values; the public sheet
 *   renders rows verbatim so the projection never leaks a raw coefficient.
 *   The optional `tone` is a display hint (a `total` subtotal / `pay` rouble
 *   row) the web sheet may emphasise; the PDF may ignore it.
 */
export const CommercialProposalPublicLineSchema = z
  .object({
    label: z.string(),
    value: z.string(),
    tone: z.enum(['total', 'pay']).optional(),
  })
  .strict();
export type CommercialProposalPublicLine = z.infer<typeof CommercialProposalPublicLineSchema>;

/**
 * @aiPurpose PUBLIC projection of a published proposal served on the
 *   token-authorized customer sheet (#1194). This is the ONLY shape a
 *   public endpoint may return — never a raw row or raw content JSON.
 *
 *   INCLUDES (customer-facing): date, addressee; the displayed CNY/USD
 *   rates frozen in the snapshot; product name + ordered characteristics;
 *   a photo-PRESENCE flag (`hasPhoto`) only — the bytes are fetched from the
 *   token-validated streaming media endpoint, never as a URL; the
 *   payment-scheme badge/label; customer-facing buyout/logistics rows; the
 *   displayed calculated totals from the snapshot; the validity sentence; the
 *   notes and manager signature/contact.
 *
 * @aiSecurity EXCLUDED by contract — adding any of these is a data-leak
 *   regression the public-DTO exclusion test guards:
 *     - raw content JSON;
 *     - the no-VAT / VAT divisors and the resolved `paymentDivisor`;
 *     - the insurance CNY/USD rate;
 *     - any storage object key — the sheet ships only `hasPhoto`, a boolean
 *       presence flag; the raw `photoObjectKey` stays forbidden and the bytes
 *       are streamed through the token-validated `POST /public/.../photo`
 *       endpoint (#1196), never a durable/presigned URL;
 *     - the public-token storage fields (hash / recoverable token);
 *     - owner / admin ids and internal audit / analytics metadata.
 * @aiInvariant `.strict()` so a future additive field is a conscious,
 *   reviewed change rather than an accidental passthrough of a row column.
 */
export const CommercialProposalPublicDtoSchema = z
  .object({
    /// Issue date as shown to the customer (display string from snapshot).
    proposalDate: z.string(),
    /// Addressee ("Кому"). Empty string when the proposal carries none.
    client: z.string(),
    /// Displayed frozen rates (formatted strings, e.g. "12.30 ₽").
    cnyRateDisplay: z.string(),
    usdRateDisplay: z.string(),
    productName: z.string(),
    /// Ordered plain-text characteristics lines.
    characteristics: z.array(z.string()),
    /// Whether the published proposal carries a product photo (#1196). The
    /// bytes are fetched separately from the token-validated streaming media
    /// endpoint; this flag NEVER carries the raw `photoObjectKey` or a URL.
    hasPhoto: z.boolean(),
    /// Payment scheme code plus its customer-facing badge label.
    paymentScheme: CommercialProposalPaymentSchemeSchema,
    paymentSchemeBadge: z.string(),
    /// Buyout / logistics customer-facing rows (label + formatted value).
    buyoutTerm: z.string(),
    buyoutLines: z.array(CommercialProposalPublicLineSchema),
    /// Logistics section heading, destination-aware (e.g. "Логистика — Москва").
    logisticsTitle: z.string(),
    logisticsTerm: z.string(),
    logisticsLines: z.array(CommercialProposalPublicLineSchema),
    /// Displayed totals from the frozen snapshot (formatted strings).
    buyTotalDisplay: z.string(),
    logisticsTotalDisplay: z.string(),
    grandTotalDisplay: z.string(),
    /// Validity sentence shown under the totals (display copy from snapshot).
    validity: z.string(),
    /// Notes + signature (plain text; newlines preserved).
    buyoutNotes: z.string(),
    logisticsNotes: z.string(),
    managerName: z.string(),
    managerPhone: z.string(),
  })
  .strict();
export type CommercialProposalPublicDto = z.infer<typeof CommercialProposalPublicDtoSchema>;

/**
 * @aiSecurity field names that MUST NEVER appear in the public projection.
 *   Exported so the exclusion test and any future projection helper assert
 *   the boundary against one source of truth.
 */
export const COMMERCIAL_PROPOSAL_PUBLIC_FORBIDDEN_FIELDS = [
  'noVatDivisor',
  'vatDivisor',
  'paymentDivisor',
  'insuranceCnyPerUsdRate',
  'photoObjectKey',
  'publicTokenHash',
  'publicToken',
  'ownerId',
  'publishedById',
  'revokedById',
  // #2311: the CRM Lead link is an internal owner/admin fact — the public
  // customer sheet never carries it.
  'leadId',
] as const;
